import React, { useMemo, useState } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Play, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';
import { pillarById, humanDuration } from '../features/cognition/utils/format';
import { useTrialRunner } from '../features/cognition/runner/useTrialRunner';
import { createTrainingDriver } from '../features/cognition/runner/drivers/trainingDriver';
import { taskById } from '../features/cognition/runner/tasks';
import RunnerShell from '../features/cognition/runner/components/RunnerShell';
import TrainingResults from '../features/cognition/runner/components/TrainingResults';
import TaskExamples from '../features/cognition/runner/components/TaskExamples';

const EMPTY_TASK = {
  targets: [],
  timing: () => ({ fixationMs: 0, windowMs: 0, gapMs: 0 }),
  toAnswer: () => ({}),
  practiceExpectation: () => null,
  StimulusView: () => null,
};

// Deliberate narrow, centered game-layout measures — not the dashboard frame
// scale (--frame-dashboard / --frame-prose). This page is a single focused
// game card, not a data-dense page, so it stays intentionally narrower than
// --frame-prose. Pulled into named constants so the intent is documented once
// instead of repeating bare literals (see the auth-card frames in Login.jsx
// for the same pattern).
const GAME_CARD_MAX_WIDTH = 720; // outer page column
const GAME_ICON_TILE_SIZE = 72; // accent icon tile above the game title
const GAME_BLURB_MAX_WIDTH = 460; // one-line session summary
const GAME_INSTRUCTIONS_MAX_WIDTH = 360; // instruction list
const GAME_FOOTNOTE_MAX_WIDTH = 380; // "training builds the habit" footnote

export default function GScoreTrainGamePage() {
  const { gameId } = useParams();
  const location = useLocation();
  // The task registry is the single source of truth for what this page can run
  // AND how it is described. Anything the registry cannot answer either comes
  // from the backend (duration) or is not shown.
  const runnerTask = taskById(gameId);
  const primaryPillar = pillarById(runnerTask?.pillar);
  const accent = primaryPillar?.accent || 'var(--accent-primary)';
  const accentWash = primaryPillar?.accentWash || 'var(--accent-light)';
  // Backend-supplied, carried over from the routine entry on the dashboard.
  // Absent on a direct link or refresh, in which case the copy just omits it.
  const durationSeconds = location.state?.durationSeconds ?? null;

  const navigate = useNavigate();
  const [running, setRunning] = useState(false);
  const driver = useMemo(() => createTrainingDriver({ gameId }), [gameId]);
  const runner = useTrialRunner({ driver, task: runnerTask || EMPTY_TASK });

  // Fire-and-forget on purpose. abandon() flips the runner to 'idle'
  // synchronously and only then awaits the POST, which it swallows the errors
  // of — the user is leaving either way. Awaiting it kept this page mounted for
  // the life of that request (apiFetch allows 30s) with phase already 'idle',
  // so the render guard below dropped RunnerShell and repainted the intro card
  // with a live "Start session" button. A second training session could be
  // started from there, and the pending navigate() would then yank the user out
  // of it mid-trial, orphaning that session too.
  const exit = () => {
    runner.abandon();
    navigate('/g-score');
  };

  if (running && runner.phase === 'complete') {
    return <TrainingResults blockStats={runner.blockStats} summary={runner.sessionSummary} />;
  }
  if (running && runnerTask && runner.phase !== 'idle') {
    return <RunnerShell task={runnerTask} runner={runner} title={runnerTask.name} onExit={exit} />;
  }

  if (!runnerTask) {
    return (
      <div style={{ maxWidth: GAME_CARD_MAX_WIDTH, margin: '0 auto', padding: 'var(--space-2) var(--space-1) var(--space-6)' }}>
        <BackLink />
        <div
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-panel)',
            padding: 'var(--space-6)',
            textAlign: 'center',
            color: 'var(--text-secondary)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--text-panel-size)',
              fontWeight: 700,
              color: 'var(--text-primary)',
              marginBottom: 'var(--space-2)',
            }}
          >
            This game isn't available
          </div>
          This training game is not available in the current version of the app. Please check for
          an update.
        </div>
      </div>
    );
  }

  // Duration is only stated when the backend supplied one — never guessed.
  const blurb =
    (durationSeconds != null
      ? `A ${humanDuration(durationSeconds)} adaptive session.`
      : 'An adaptive session.') +
    ' Difficulty adjusts trial-by-trial to keep the challenge honest.';

  return (
    <>
      <SEO title={runnerTask.name} path={`/g-score/train/${gameId}`} noindex />
      <div style={{ maxWidth: GAME_CARD_MAX_WIDTH, margin: '0 auto', padding: 'var(--space-2) var(--space-1) var(--space-6)' }}>
        <BackLink />

        <div
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-overlay)',
            padding: 'var(--space-6)',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: GAME_ICON_TILE_SIZE,
              height: GAME_ICON_TILE_SIZE,
              borderRadius: 'var(--radius-overlay)',
              background: accentWash,
              color: accent,
              margin: '0 auto var(--space-4)',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Sparkles size={30} strokeWidth={1.5} />
          </div>

          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-1) var(--space-3)',
              borderRadius: 'var(--radius-full)',
              background: accentWash,
              color: accent,
              fontSize: 'var(--text-label-size)',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 'var(--text-label-tracking)',
              marginBottom: 'var(--space-3)',
            }}
          >
            {primaryPillar?.label || 'Cognition'}
          </div>

          <h1
            style={{
              margin: '0 0 var(--space-2)',
              fontSize: 'var(--text-section-size)',
              fontWeight: 700,
              color: 'var(--text-primary)',
              letterSpacing: 'var(--text-section-tracking)',
            }}
          >
            {runnerTask.name}
          </h1>
          <p
            style={{
              margin: '0 auto var(--space-5)',
              maxWidth: GAME_BLURB_MAX_WIDTH,
              fontSize: 'var(--text-body-size)',
              color: 'var(--text-secondary)',
              lineHeight: 1.55,
            }}
          >
            {blurb}
          </p>

          <ul
            style={{
              margin: '0 auto var(--space-5)',
              maxWidth: GAME_INSTRUCTIONS_MAX_WIDTH,
              padding: 0,
              listStyle: 'none',
              display: 'grid',
              gap: 'var(--space-2)',
              fontSize: 'var(--text-body-size)',
              color: 'var(--text-secondary)',
              textAlign: 'left',
            }}
          >
            {runnerTask.instructions.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>

          <TaskExamples task={runnerTask} />

          <button
            type="button"
            style={{
              padding: 'var(--space-3) var(--space-5)',
              borderRadius: 'var(--radius-control)',
              border: 'none',
              background: 'var(--btn-primary-bg)',
              color: 'var(--btn-primary-text)',
              fontSize: 'var(--text-body-size)',
              fontWeight: 700,
              fontFamily: 'inherit',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
            }}
            onClick={() => {
              setRunning(true);
              runner.start();
            }}
          >
            <Play size={16} strokeWidth={1.5} fill="var(--btn-primary-text)" />
            Start session
          </button>

          <p
            style={{
              margin: 'var(--space-4) auto 0',
              maxWidth: GAME_FOOTNOTE_MAX_WIDTH,
              fontSize: 'var(--text-label-size)',
              color: 'var(--text-tertiary)',
              lineHeight: 1.5,
            }}
          >
            Training builds the habit. Only assessments measure your progress.
          </p>
        </div>
      </div>
    </>
  );
}

function BackLink() {
  return (
    <Link
      to="/g-score"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
        color: 'var(--text-secondary)',
        textDecoration: 'none',
        fontSize: 'var(--text-body-size)',
        fontWeight: 600,
        marginBottom: 'var(--space-4)',
      }}
    >
      <ArrowLeft size={14} strokeWidth={1.5} />
      Back to G-Score
    </Link>
  );
}
